import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { forSaleListings, cityOf } from "../mock";
import PropertyCard from "../components/heider/PropertyCard";
import ContactForm from "../components/heider/ContactForm";
import Footer from "../components/heider/Footer";
import useReveal from "../hooks/useReveal";
import { ArrowRight, X } from "lucide-react";
import ScrollCue from "../components/heider/ScrollCue";
import ConsultationCTA from "../components/heider/ConsultationCTA";

const parsePrice = (p) => (p ? Number(String(p).replace(/[^0-9]/g, "")) : 0);

const PRICE_FILTERS = [
  { key: "all", label: "All Prices", test: () => true },
  { key: "u500", label: "Under $500K", test: (v) => v < 500000 },
  { key: "500-1m", label: "$500K – $1M", test: (v) => v >= 500000 && v < 1000000 },
  { key: "1m", label: "$1M+", test: (v) => v >= 1000000 },
];

const BuyPage = () => {
  const [priceFilter, setPriceFilter] = useState("all");
  const [minBeds, setMinBeds] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const area = searchParams.get("area") || "";

  const clearArea = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("area");
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    const pf = PRICE_FILTERS.find((f) => f.key === priceFilter) || PRICE_FILTERS[0];
    return forSaleListings.filter(
      (x) =>
        pf.test(parsePrice(x.price)) &&
        (x.beds || 0) >= minBeds &&
        (!area || cityOf(x.address).toLowerCase() === area.toLowerCase())
    );
  }, [priceFilter, minBeds, area]);

  useReveal(filtered);
  return (
    <div className="page-in">
      {/* Hero */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden bg-black">
        <img
          src="https://images.pexels.com/photos/8143683/pexels-photo-8143683.jpeg?auto=compress&cs=tinysrgb&w=2000"
          alt="Buy"
          className="absolute inset-0 w-full h-full object-cover animate-kenburns"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative text-center text-white px-6">
          <h1 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl leading-tight clip-line">
            <span style={{ opacity: 0 }}>Your Home is Just the Start</span>
          </h1>
          <p className="mt-6 text-[12px] md:text-sm tracking-luxe uppercase text-white/80 fade-in" style={{ animationDelay: "0.6s", opacity: 0 }}>
            A Market Like No Other Deserves Representation Like No Other
          </p>
        </div>
        <ScrollCue />
      </section>

      <ConsultationCTA type="buy" />

      {/* Acquire with confidence */}
      <section className="bg-[#f3f1ec] py-24 md:py-32">
        <div className="max-w-[900px] mx-auto px-6 text-center reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-6">Acquire with Confidence</p>
          <p className="font-serif-display text-2xl md:text-[2rem] leading-relaxed text-[#2a2a2a] font-light">
            Whether you're preparing to acquire a residence or bring a property to market, CAPITAL CORNER delivers a refined, end-to-end experience tailored to your objectives. Our disciplined process ensures that every detail is expertly managed — allowing you to stay focused on the bigger picture.
          </p>
        </div>

        {/* Exclusive Listings */}
        <div className="max-w-[1700px] mx-auto px-6 md:px-10 mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 reveal-left gap-6">
            <div>
              <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-3">On the Market</p>
              <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1a1a1a]">{filtered.length} Active {filtered.length === 1 ? "Listing" : "Listings"}{area ? ` in ${area}` : ""}</h2>
              {area && (
                <button
                  data-testid="clear-area-filter"
                  onClick={clearArea}
                  className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-[#1a1a1a] bg-[#b4936a]/15 border border-[#b4936a]/40 rounded-full px-4 py-1.5 hover:bg-[#b4936a]/25 transition-colors"
                >
                  {area} <X size={12} />
                </button>
              )}
            </div>
            <a href="https://www.zillow.com/profile/Omer%20Reshid" target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-[#1a1a1a] border-b border-[#1a1a1a] pb-1 hover:text-[#b4936a] hover:border-[#b4936a] transition-colors">View All on Zillow <ArrowRight size={14} /></a>
          </div>

          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mb-12 pb-8 border-b border-[#1a1a1a]/10 reveal" data-testid="listing-filter-bar">
            <div className="flex flex-wrap gap-2" data-testid="price-filter-group">
              {PRICE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`price-filter-${f.key}`}
                  onClick={() => setPriceFilter(f.key)}
                  className={`px-5 py-2 rounded-full text-[11px] tracking-luxe uppercase transition-all duration-300 border ${
                    priceFilter === f.key
                      ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                      : "bg-transparent text-[#1a1a1a] border-[#1a1a1a]/25 hover:border-[#b4936a] hover:text-[#b4936a]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 md:ml-auto" data-testid="beds-filter-group">
              <span className="text-[10px] tracking-luxe uppercase text-neutral-500 mr-1">Beds</span>
              {[0, 2, 3, 4, 5].map((b) => (
                <button
                  key={b}
                  data-testid={`beds-filter-${b}`}
                  onClick={() => setMinBeds(b)}
                  className={`w-9 h-9 rounded-full text-[12px] transition-all duration-300 border ${
                    minBeds === b
                      ? "bg-[#b4936a] text-white border-[#b4936a]"
                      : "bg-transparent text-[#1a1a1a] border-[#1a1a1a]/25 hover:border-[#b4936a] hover:text-[#b4936a]"
                  }`}
                >
                  {b === 0 ? "Any" : `${b}+`}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-24 text-center reveal" data-testid="no-results">
              <p className="font-serif-display text-2xl text-[#1a1a1a] mb-3">No listings match your filters</p>
              <button
                data-testid="reset-filters"
                onClick={() => { setPriceFilter("all"); setMinBeds(0); clearArea(); }}
                className="text-[11px] tracking-luxe uppercase text-[#b4936a] border-b border-[#b4936a] pb-1"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {filtered.map((item) => (
                <div key={item.id} className="reveal-scale">
                  <PropertyCard item={item} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Knowledge / CTA */}
      <section className="bg-[#0d0d0d] text-white py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div className="reveal">
            <h3 className="font-serif-display text-4xl md:text-5xl font-light mb-4">Knowledge is Power</h3>
            <p className="text-white/55 leading-relaxed mb-6">Want to stay on top of the local market? Our customized market insights make it easy to discover the latest listings, trends, and so much more.</p>
            <a href="#contact" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase border-b border-white pb-1 hover:text-[#b4936a] hover:border-[#b4936a] transition-colors">Customized Market Insights <ArrowRight size={14} /></a>
          </div>
          <div className="reveal">
            <h3 className="font-serif-display text-4xl md:text-5xl font-light mb-4">Ready to Find Your New Home?</h3>
            <p className="text-white/55 leading-relaxed mb-6">Let CAPITAL CORNER guide you to the region's most coveted addresses with discretion and precision.</p>
            <a href="#contact" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase border-b border-white pb-1 hover:text-[#b4936a] hover:border-[#b4936a] transition-colors">Schedule a Showing <ArrowRight size={14} /></a>
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default BuyPage;
